import type { WebhookOperation } from '@compass/client/types/webhook';
import { WebhookOperationState } from '@compass/client/types/webhook';
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import React from 'react';

import { Progress } from '@/components/ui/progress';

import { useWebhookOperation } from '../hooks/useWebhookOperation';
import {
  isOperationRunning,
  operationProgressPercent,
  operationStateLabel,
  webhookErrorMessage,
} from '../utils';

interface OperationProgressProps {
  organizationId: string;
  /** The operation as returned when it was started; shown until the first poll lands. */
  operation: WebhookOperation;
  /** False when the viewer may not poll operations; the initial snapshot is shown. */
  canPoll: boolean;
}

/** Live progress of a bulk retry, polled every 2 s until it finishes. */
export function OperationProgress({ organizationId, operation, canPoll }: OperationProgressProps) {
  const query = useWebhookOperation(organizationId, operation.id ?? '', canPoll);
  const op = query.data ?? operation;
  const running = isOperationRunning(op);
  const failed = op.state === WebhookOperationState.WEBHOOK_OPERATION_STATE_FAILED;
  const total = op.total ?? 0;
  const started = op.started ?? 0;
  const skipped = op.skipped ?? 0;

  return (
    <div className="space-y-3 rounded-md border px-4 py-3" aria-live="polite">
      <div className="flex items-center gap-2 text-sm font-medium">
        {failed ? (
          <AlertCircle className="h-4 w-4 text-destructive" />
        ) : running ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <CheckCircle2 className="h-4 w-4 text-primary" />
        )}
        {operationStateLabel(op.state)}
      </div>
      <Progress value={operationProgressPercent(op)} />
      <p className="text-xs text-muted-foreground">
        {started.toLocaleString('en-US')} started
        {skipped > 0 && `, ${skipped.toLocaleString('en-US')} skipped`} of{' '}
        {total.toLocaleString('en-US')}
      </p>
      {failed && op.error && <p className="text-xs text-destructive">{op.error}</p>}
      {query.isError && (
        <p className="text-xs text-muted-foreground">
          {webhookErrorMessage(query.error, 'Could not refresh progress.')}
        </p>
      )}
    </div>
  );
}
